import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardFooter, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { MarkdownRenderer } from '@/components/MarkdownRenderer';
import { ArrowLeft, Eye, PenLine, Image, Bold, Italic, Link, List, ListOrdered, Code, Quote, Info, Settings, Sparkles, FileCheck, Plus, BookmarkCheck, Check, Copy, Mail, BookOpen, Building2, PlayCircle } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { aiService } from '@/services/aiService';
import { useAuth } from '@/contexts/AuthContext';

// Vordefinierte Kategorien für die Auswahl
const predefinedCategories = [
  'Bewusstes Konsumieren',
  'Sparen',
  'Minimalismus',
  'Nachhaltigkeit',
  'Finanzen',
  'Psychologie',
  'Digitales Leben',
  'Lifestyle',
  'Praktische Tipps',
  'Erfahrungsbericht'
];

// Editor Toolbar Komponenten hinzufügen
const EditorToolbar = ({ editor, onBold, onItalic, onH2, onH3, onUnorderedList, onOrderedList, onQuote, onLink, onImage, onCode }: {
  editor: React.RefObject<HTMLTextAreaElement>;
  onBold: () => void;
  onItalic: () => void;
  onH2: () => void;
  onH3: () => void;
  onUnorderedList: () => void;
  onOrderedList: () => void;
  onQuote: () => void;
  onLink: () => void;
  onImage: () => void;
  onCode: () => void;
}) => {
  return (
    <div className="flex flex-wrap gap-1 p-2 bg-gray-50 border border-b-0 rounded-t-md">
      <Button 
        type="button" 
        variant="ghost" 
        size="sm" 
        className="p-1 h-8 w-8" 
        onClick={onBold}
        title="Fett"
      >
        <span className="font-bold">B</span>
      </Button>
      <Button 
        type="button" 
        variant="ghost" 
        size="sm" 
        className="p-1 h-8 w-8" 
        onClick={onItalic}
        title="Kursiv"
      >
        <span className="italic">I</span>
      </Button>
      <Button 
        type="button" 
        variant="ghost" 
        size="sm" 
        className="p-1 h-8" 
        onClick={onH2}
        title="Überschrift 2"
      >
        <span className="font-bold">H2</span>
      </Button>
      <Button 
        type="button" 
        variant="ghost" 
        size="sm" 
        className="p-1 h-8" 
        onClick={onH3}
        title="Überschrift 3"
      >
        <span className="font-bold">H3</span>
      </Button>
      <Button 
        type="button" 
        variant="ghost" 
        size="sm" 
        className="p-1 h-8" 
        onClick={onUnorderedList}
        title="Ungeordnete Liste"
      >
        <ul className="text-xs px-1">
          <li>•</li>
          <li>•</li>
        </ul>
      </Button>
      <Button 
        type="button" 
        variant="ghost" 
        size="sm" 
        className="p-1 h-8" 
        onClick={onOrderedList}
        title="Geordnete Liste"
      >
        <ol className="text-xs px-1">
          <li>1.</li>
          <li>2.</li>
        </ol>
      </Button>
      <Button 
        type="button" 
        variant="ghost" 
        size="sm" 
        className="p-1 h-8" 
        onClick={onQuote}
        title="Zitat"
      >
        <span className="text-lg">"</span>
      </Button>
      <Button 
        type="button" 
        variant="ghost" 
        size="sm" 
        className="p-1 h-8" 
        onClick={onLink}
        title="Link"
      >
        <span className="underline">URL</span>
      </Button>
      <Button 
        type="button" 
        variant="ghost" 
        size="sm" 
        className="p-1 h-8" 
        onClick={onImage}
        title="Bild"
      >
        <span>🖼️</span>
      </Button>
      <Button 
        type="button" 
        variant="ghost" 
        size="sm" 
        className="p-1 h-8" 
        onClick={onCode}
        title="Code"
      >
        <span className="font-mono">&lt;/&gt;</span>
      </Button>
    </div>
  );
};

const CreateBlogPost = () => {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const [title, setTitle] = useState('');
  const [excerpt, setExcerpt] = useState('');
  const [content, setContent] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [categories, setCategories] = useState<string[]>([]);
  const [newCategory, setNewCategory] = useState('');
  const [activeTab, setActiveTab] = useState('edit');
  const [selectionStart, setSelectionStart] = useState(0);
  const [selectionEnd, setSelectionEnd] = useState(0);
  const [isAiProcessing, setIsAiProcessing] = useState(false);
  const [remainingApiRequests, setRemainingApiRequests] = useState<number>(0);
  const [isPreview, setIsPreview] = useState(false);
  const [email, setEmail] = useState('');
  const [isExporting, setIsExporting] = useState(false);
  const [exportSuccess, setExportSuccess] = useState(false);
  
  // Refs für Editor und Cursor-Position
  const editorRef = useRef<HTMLTextAreaElement>(null);
  const lastCursorPosition = useRef<{ start: number; end: number }>({ start: 0, end: 0 });

  // Aktualisierung der Selektion, wenn die Textarea fokussiert ist
  useEffect(() => {
    const textarea = editorRef.current; // Verwende editorRef statt textareaRef
    
    const handleSelectionChange = () => {
      if (textarea) {
        setSelectionStart(textarea.selectionStart);
        setSelectionEnd(textarea.selectionEnd);
      }
    };
    
    if (textarea) {
      textarea.addEventListener('select', handleSelectionChange);
      textarea.addEventListener('click', handleSelectionChange);
      textarea.addEventListener('keyup', handleSelectionChange);
      
      return () => {
        textarea.removeEventListener('select', handleSelectionChange);
        textarea.removeEventListener('click', handleSelectionChange);
        textarea.removeEventListener('keyup', handleSelectionChange);
      };
    }
  }, [isPreview]); // Abhängigkeit hinzufügen, damit es sich aktualisiert wenn die Vorschau gewechselt wird

  // Aktualisiere die verbleibenden API-Anfragen
  const updateRemainingRequests = () => {
    if (aiService.hasApiKey()) {
      const remaining = aiService.getRemainingRequests();
      setRemainingApiRequests(remaining);
    }
  };

  // Lokale Speicherung einrichten und API-Limits laden (nur beim ersten Laden)
  useEffect(() => {
    // Gespeicherte Daten laden
    const savedTitle = localStorage.getItem('kaufpause-draft-title');
    const savedExcerpt = localStorage.getItem('kaufpause-draft-excerpt');
    const savedContent = localStorage.getItem('kaufpause-draft-content');
    const savedCategories = localStorage.getItem('kaufpause-draft-categories');
    const savedImageUrl = localStorage.getItem('kaufpause-draft-imageUrl');
    const savedEmail = localStorage.getItem('kaufpause-draft-email');
    
    if (savedTitle) setTitle(savedTitle);
    if (savedExcerpt) setExcerpt(savedExcerpt);
    if (savedContent) setContent(savedContent);
    if (savedCategories) setCategories(JSON.parse(savedCategories));
    if (savedImageUrl) setImageUrl(savedImageUrl);
    if (savedEmail) setEmail(savedEmail);
    
    // API-Limits laden
    updateRemainingRequests();
  }, []); // Leere Dependency-Array - nur beim ersten Laden ausführen
    
  // Separater useEffect für auto-save mit stabiler Referenz
  useEffect(() => {
    // Speichern bei Änderungen
    const saveInterval = setInterval(() => {
      if (title || excerpt || content || categories.length > 0 || imageUrl || email) {
        saveToLocalStorage();
      }
    }, 30000); // Alle 30 Sekunden speichern
    
    return () => clearInterval(saveInterval);
  }, [title, excerpt, content, categories, imageUrl, email]);
  
  // Speichert die aktuellen Formularwerte im LocalStorage (mit useCallback optimiert)
  const saveToLocalStorage = useCallback(() => {
    localStorage.setItem('kaufpause-draft-title', title);
    localStorage.setItem('kaufpause-draft-excerpt', excerpt);
    localStorage.setItem('kaufpause-draft-content', content);
    localStorage.setItem('kaufpause-draft-categories', JSON.stringify(categories));
    localStorage.setItem('kaufpause-draft-imageUrl', imageUrl);
    localStorage.setItem('kaufpause-draft-email', email);
  }, [title, excerpt, content, categories, imageUrl, email]);
  
  // Löscht alle gespeicherten Entwürfe
  const clearLocalStorage = () => {
    localStorage.removeItem('kaufpause-draft-title');
    localStorage.removeItem('kaufpause-draft-excerpt');
    localStorage.removeItem('kaufpause-draft-content');
    localStorage.removeItem('kaufpause-draft-categories');
    localStorage.removeItem('kaufpause-draft-imageUrl');
    localStorage.removeItem('kaufpause-draft-email');
  };
  
  // Cursor-Position speichern wenn sich der Fokus ändert
  const handleEditorSelect = () => {
    if (editorRef.current) {
      lastCursorPosition.current = {
        start: editorRef.current.selectionStart,
        end: editorRef.current.selectionEnd,
      };
    }
  };
  
  // Formatierungsfunktionen
  const insertTextAtCursor = (before: string, after: string = '') => {
    if (!editorRef.current) return;
    
    const textarea = editorRef.current;
    const { start, end } = lastCursorPosition.current;
    const selectedText = content.substring(start, end);
    
    const newText = content.substring(0, start) + before + selectedText + after + content.substring(end);
    setContent(newText);
    
    // Fokus wieder auf Textarea setzen und Cursor an die richtige Position
    setTimeout(() => {
      if (textarea) {
        textarea.focus();
        // Cursor nach dem eingefügten Text positionieren
        const newPosition = start + before.length + selectedText.length + after.length;
        textarea.setSelectionRange(newPosition, newPosition);
        // Aktualisiere lastCursorPosition
        lastCursorPosition.current = { start: newPosition, end: newPosition };
      }
    }, 0);
  };
  
  const handleBold = () => insertTextAtCursor('**', '**');
  const handleItalic = () => insertTextAtCursor('*', '*');
  const handleH2 = () => insertTextAtCursor('## ');
  const handleH3 = () => insertTextAtCursor('### ');
  const handleUnorderedList = () => insertTextAtCursor('- ');
  const handleOrderedList = () => insertTextAtCursor('1. ');
  const handleQuote = () => insertTextAtCursor('> ');
  const handleLink = () => insertTextAtCursor('[', '](https://)');
  const handleImage = () => insertTextAtCursor('![alt text](', ')');
  const handleCode = () => insertTextAtCursor('```\n', '\n```');

  // Funktion zum Hinzufügen einer Kategorie mit verbessertem Design
  const addCategory = () => {
    if (!newCategory.trim()) return;
    
    if (!categories.includes(newCategory.trim())) {
      setCategories([...categories, newCategory.trim()]);
    }
    
    setNewCategory('');
  };

  // Kategorie aus vordefinierter Liste auswählen
  const handleSelectCategory = (category: string) => {
    if (!categories.includes(category)) {
      setCategories([...categories, category]);
    }
  };

  // Kategorie entfernen
  const removeCategory = (categoryToRemove: string) => {
    setCategories(categories.filter(cat => cat !== categoryToRemove));
  };

  // AI-Verarbeitung für verschiedene Modi
  const processWithAI = async (mode: 'summarize' | 'optimize' | 'format' | 'title') => {
    if (!content.trim()) {
      toast.error('Bitte zuerst Inhalt eingeben');
      return;
    }
    
    setIsAiProcessing(true);
    let textToProcess = content;
    let processedContent = '';
    
    try {
      if (mode === 'summarize') {
        // Erstellt eine SEO-optimierte Kurzbeschreibung aus dem Inhalt
        processedContent = await aiService.optimizeBlogContent({
          content: textToProcess,
          mode: 'seo',
          maxLength: 30 // Ungefähre Wortanzahl (ca. 150-160 Zeichen)
        });
        
        // Entferne alle Anführungszeichen und andere unerwünschte Zeichen
        processedContent = processedContent
          .replace(/["'""'']/g, '') // Alle Arten von Anführungszeichen entfernen
          .replace(/^[:\-–—.,;]+|[:\-–—.,;]+$/g, '') // Löscht Satzzeichen am Anfang und Ende
          .trim();
        
        setExcerpt(processedContent);
        toast.success('SEO-optimierte Kurzbeschreibung wurde generiert');
      } else if (mode === 'title') {
        // Titel aus dem Inhalt generieren
        processedContent = await aiService.optimizeBlogContent({
          content: textToProcess,
          mode: 'title',
          maxLength: 10 // Ungefähre Wortanzahl für Titel
        });
        
        // Entferne alle Anführungszeichen und andere unerwünschte Zeichen
        processedContent = processedContent
          .replace(/["'""'']/g, '') // Alle Arten von Anführungszeichen entfernen
          .replace(/^[:\-–—.,;]+|[:\-–—.,;]+$/g, '') // Löscht Satzzeichen am Anfang und Ende
          .trim();
        
        setTitle(processedContent);
        toast.success('Titel wurde aus dem Inhalt generiert');
      } else if (mode === 'optimize') {
        // Optimiert den gesamten Text
        processedContent = await aiService.optimizeBlogContent({
          content: textToProcess,
          mode: 'optimize'
        });
        setContent(processedContent);
        toast.success('Text wurde optimiert');
      } else if (mode === 'format') {
        // Formatiert den Text in korrektes Markdown
        processedContent = await aiService.optimizeBlogContent({
          content: textToProcess,
          mode: 'format'
        });
        setContent(processedContent);
        toast.success('Text wurde formatiert');
      }
      
      // Aktualisiere die verbleibenden API-Anfragen nach erfolgreicher Anfrage
      updateRemainingRequests();
    } catch (error) {
      console.error('Fehler bei der KI-Verarbeitung:', error);
      toast.error(`KI-Fehler: ${error instanceof Error ? error.message : 'Unbekannter Fehler'}`);
    } finally {
      setIsAiProcessing(false);
    }
  };

  // Optimiertes Kategorie-Hinzufügen
  const renderCategorySelector = () => {
    return (
      <div className="mt-4">
        <Label htmlFor="categories" className="mb-1.5 block">Kategorien</Label>
        <div className="flex flex-wrap gap-2 mb-2">
          {categories.map((category, index) => (
            <Badge key={index} variant="secondary" className="gap-1 px-2 py-1 bg-emerald-50">
              {category}
              <button 
                className="ml-1 text-gray-500 hover:text-gray-700" 
                onClick={() => removeCategory(category)}
              >
                ×
              </button>
            </Badge>
          ))}
        </div>
        
        <div className="flex flex-col md:flex-row gap-2">
          {/* Dropdown für vordefinierte Kategorien */}
          <div className="w-full md:w-1/3">
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="h-10 w-full justify-start text-left">
                  Kategorie auswählen...
                </Button>
              </PopoverTrigger>
              <PopoverContent className="p-0" align="start" side="bottom">
                <Command>
                  <CommandInput placeholder="Kategorie suchen..." />
                  <CommandList>
                    <CommandEmpty>Keine Kategorie gefunden</CommandEmpty>
                    <CommandGroup heading="Vordefinierte Kategorien">
                      {predefinedCategories.map((category) => (
                        <CommandItem 
                          key={category}
                          onSelect={() => handleSelectCategory(category)}
                        >
                          {category}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>
          
          {/* Vereinfachte Eingabeform für eigene Kategorie */}
          <div className="flex-1 flex">
            <Input 
              placeholder="Eigene Kategorie hinzufügen" 
              value={newCategory}
              className="h-10 rounded-r-none border-r-0"
              onChange={e => setNewCategory(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addCategory();
                }
              }}
            />
            <Button 
              variant="default"
              className="h-10 rounded-l-none bg-emerald-600 hover:bg-emerald-700 px-3"
              type="button"
              onClick={addCategory}
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    );
  };

  // Export-Funktion für Markdown
  const handleExport = () => {
    if (!title.trim()) {
      toast.error('Bitte gib einen Titel ein');
      return;
    }

    if (!content.trim()) {
      toast.error('Der Artikel-Inhalt darf nicht leer sein');
      return;
    }
    
    try {
      // Starte Lade-Animation
      setIsExporting(true);
      
      // Simuliere eine kurze Ladezeit für besseres UX-Feedback
      setTimeout(() => {
        // Generiere einen Slug aus dem Titel
        const slug = title
          .toLowerCase()
          .replace(/[^\w\s]/gi, '')
          .replace(/\s+/g, '-');
        
        // Erstelle Markdown-Frontmatter
        const frontmatter = `---
title: "${title}"
date: "${new Date().toISOString()}"
slug: "${slug}"
excerpt: "${excerpt || title}"
imageUrl: "${imageUrl || 'https://images.unsplash.com/photo-1591696205602-2f950c417cb9?q=80&w=800&auto=format&fit=crop'}"
categories: [${categories.map(cat => `"${cat}"`).join(', ')}]
---

`;

        const fullMarkdown = frontmatter + content;
        
        // Debug-Ausgabe
        console.log("Exportiere Markdown:", fullMarkdown.substring(0, 200) + "...");
        
        // Erstelle einen Download-Link mit sicherem Pfad
        const fileName = `kaufpause-artikel-${slug}.md`;
        const fileType = 'text/markdown';
        
        try {
          // Methode 1: Standard-Download
          const blob = new Blob([fullMarkdown], { type: fileType });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = fileName;
          document.body.appendChild(a);
          a.click();
          
          // Wichtig: Cleanup
          setTimeout(() => {
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
          }, 100);
        } catch (downloadError) {
          console.error("Fehler bei Methode 1:", downloadError);
          
          try {
            // Methode 2: Alternative Download-Methode
            const element = document.createElement('a');
            const dataStr = 'data:text/markdown;charset=utf-8,' + encodeURIComponent(fullMarkdown);
            element.setAttribute('href', dataStr);
            element.setAttribute('download', fileName);
            element.style.display = 'none';
            document.body.appendChild(element);
            element.click();
            document.body.removeChild(element);
          } catch (altError) {
            console.error("Fehler bei Methode 2:", altError);
            throw new Error("Download konnte nicht durchgeführt werden");
          }
        }
        
        // Export erfolgreich
        setExportSuccess(true);
        setIsExporting(false);
        
        // E-Mail-Client nach kurzer Verzögerung öffnen
        setTimeout(() => {
          const subject = encodeURIComponent('KaufPause Blog-Artikel Einreichung');
          const body = encodeURIComponent(`Hallo KaufPause-Team,

ich möchte gerne einen Blog-Artikel einreichen. Die Markdown-Datei ist angehängt.

Titel: ${title}
Kategorien: ${categories.join(', ')}

Bei Rückfragen stehe ich gerne zur Verfügung.

Mit freundlichen Grüßen,
${email ? email : '[Dein Name]'}`);
          
          window.open(`mailto:kontakt@kaufpause.at?subject=${subject}&body=${body}`, '_blank');
        }, 1000);
      }, 1500); // Kurze Verzögerung für besseres UX-Feedback
    } catch (error) {
      console.error('Fehler beim Exportieren:', error);
      toast.error(`Fehler beim Exportieren: ${error instanceof Error ? error.message : 'Unbekannter Fehler'}`);
      setIsExporting(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50/50">
      {/* Navigation - identisch zur Startseite */}
      <nav className="fixed top-0 left-0 right-0 bg-white/80 backdrop-blur-md border-b border-gray-100 z-50">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <a 
              href="/" 
              className="flex items-center gap-2 hover:scale-105 transition-all duration-300 cursor-pointer group"
            >
              <img 
                src="/Favicon/favicon-196x196.png" 
                alt="KaufPause Logo" 
                className="w-8 h-8 -translate-y-0.5"
              />
              <span className="text-xl font-bold text-gray-900">
                Kauf<span className="bg-gradient-to-r from-emerald-600 to-emerald-700 bg-clip-text text-transparent">Pause</span>
              </span>
            </a>
          </div>
          
          <div className="hidden md:flex items-center justify-center gap-8 absolute left-1/2 transform -translate-x-1/2">
            <button
              onClick={() => navigate('/blog')}
              className="text-gray-600 hover:text-emerald-600 transition-colors duration-200 font-medium hover:bg-emerald-50 px-3 py-2 rounded-lg flex items-center gap-2"
            >
              <BookOpen className="w-4 h-4" />
              Blog
            </button>
            <button
              onClick={() => navigate('/institutionen')}
              className="text-gray-600 hover:text-emerald-600 transition-colors duration-200 font-medium hover:bg-emerald-50 px-3 py-2 rounded-lg flex items-center gap-2"
            >
              <Building2 className="w-4 h-4" />
              Für Institutionen
            </button>
            {!user && (
              <button
                onClick={() => navigate('/demo')}
                className="text-gray-600 hover:text-emerald-600 transition-colors duration-200 font-medium hover:bg-emerald-50 px-3 py-2 rounded-lg flex items-center gap-2"
              >
                <PlayCircle className="w-4 h-4" />
                Demo testen
              </button>
            )}
          </div>

          <div className="flex items-center gap-3">
            {user ? (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleSignOut}
                  className="text-gray-600 hover:text-gray-800 hidden sm:flex"
                >
                  Abmelden
                </Button>
                <Button
                  className="bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-white shadow-md hover:shadow-lg transition-all duration-300"
                  onClick={() => navigate('/dashboard')}
                >
                  Dashboard
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="ghost"
                  className="text-gray-600 hover:text-emerald-600 hidden sm:flex"
                  onClick={() => navigate('/auth')}
                >
                  Anmelden
                </Button>
                <Button
                  className="bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-white shadow-md hover:shadow-lg transition-all duration-300"
                  onClick={() => navigate('/auth')}
                >
                  Kostenlos starten
                </Button>
              </>
            )}
          </div>
        </div>
      </nav>

      <div className="container max-w-4xl mx-auto px-4 pt-24 pb-16">
        {/* Back button */}
        <Button 
          variant="ghost" 
          className="mb-8 flex items-center gap-2 text-emerald-600 hover:text-emerald-700 rounded-xl"
          onClick={() => navigate('/blog')}
        >
          <ArrowLeft className="h-4 w-4" />
          Zurück zur Übersicht
        </Button>

        <Card className="border border-gray-200 shadow-sm mb-8 rounded-2xl">
          <CardHeader className="pb-4">
            <CardTitle className="text-2xl">Artikel erstellen</CardTitle>
            <CardDescription className="flex items-center justify-between">
              <span>Verfasse einen neuen Artikel für den KaufPause Blog</span>
              {aiService.hasApiKey() && (
                <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 gap-1">
                  <Sparkles className="h-3 w-3" />
                  <span>{remainingApiRequests} von 10 KI-Anfragen pro Stunde verfügbar</span>
                </Badge>
              )}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Titel */}
            <div>
              <Label htmlFor="title">Titel</Label>
              <div className="flex gap-2">
                <Input 
                  id="title" 
                  placeholder="Titel des Blogartikels" 
                  value={title} 
                  onChange={e => setTitle(e.target.value)}
                  className="flex-1 rounded-xl border-gray-200 focus:border-emerald-300 focus:ring-emerald-200"
                />
                <Button 
                  variant="outline" 
                  size="icon" 
                  type="button"
                  className="h-10 w-10 text-amber-500 rounded-xl border-gray-200 hover:border-amber-300"
                  onClick={() => processWithAI('title')}
                  disabled={isAiProcessing || !content.trim()}
                  title="Titel mit KI generieren"
                >
                  <Sparkles className="h-4 w-4" />
                </Button>
              </div>
            </div>
            
            {/* Kurzbeschreibung */}
            <div className="mt-4">
              <Label htmlFor="excerpt" className="mb-1.5 block">Kurzbeschreibung</Label>
              <div className="flex gap-2">
                <Textarea 
                  id="excerpt" 
                  placeholder="Kurze Zusammenfassung des Artikels" 
                  value={excerpt} 
                  onChange={e => setExcerpt(e.target.value)}
                  className="h-20 resize-none flex-1 rounded-xl border-gray-200 focus:border-emerald-300 focus:ring-emerald-200"
                />
                <Button 
                  variant="outline" 
                  size="icon" 
                  type="button"
                  className="h-10 w-10 text-amber-500 self-start rounded-xl border-gray-200 hover:border-amber-300"
                  onClick={() => processWithAI('summarize')}
                  disabled={isAiProcessing || !content.trim()}
                  title="SEO-optimierte Kurzbeschreibung erstellen"
                >
                  <Sparkles className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Die Kurzbeschreibung sollte 150-160 Zeichen lang sein und SEO-Schlüsselwörter enthalten.
              </p>
            </div>
            
            {/* Kategorien */}
            {renderCategorySelector()}
            
            {/* Bild-URL */}
            <div className="mt-4">
              <Label htmlFor="imageUrl">Bild-URL (optional)</Label>
              <Input 
                id="imageUrl" 
                placeholder="https://beispiel.com/bild.jpg" 
                value={imageUrl} 
                onChange={e => setImageUrl(e.target.value)}
                className="rounded-xl border-gray-200 focus:border-emerald-300 focus:ring-emerald-200"
              />
              <p className="text-xs text-gray-500 mt-1">
                Gib die URL eines passenden Bildes für deinen Artikel an.
              </p>
            </div>
            
            {/* Email */}
            <div className="mt-4">
              <Label htmlFor="email">E-Mail für Rückfragen</Label>
              <Input 
                id="email" 
                type="email"
                placeholder="deine@email.com" 
                value={email} 
                onChange={e => setEmail(e.target.value)}
                className="rounded-xl border-gray-200 focus:border-emerald-300 focus:ring-emerald-200"
              />
              <p className="text-xs text-gray-500 mt-1">
                Wird nur für Rückfragen zu deinem Artikel verwendet.
              </p>
            </div>
            
            <Separator className="my-6" />
            
            {/* Markdown Editor mit Bearbeiten/Vorschau-Tabs */}
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <Label htmlFor="content">Inhalt</Label>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" className="h-8 gap-1" disabled={isAiProcessing}>
                      <Sparkles className="h-3.5 w-3.5 text-amber-500" />
                      <span>KI-Hilfe</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem 
                      disabled={isAiProcessing} 
                      onClick={() => processWithAI('format')}
                      className="cursor-pointer gap-2"
                    >
                      <FileCheck className="h-4 w-4" />
                      Markdown formatieren
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      disabled={isAiProcessing} 
                      onClick={() => processWithAI('optimize')}
                      className="cursor-pointer gap-2"
                    >
                      <Sparkles className="h-4 w-4" />
                      Text optimieren
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              
              {/* Editor/Preview Tabs innerhalb des Editors */}
              <div className="border rounded-md">
                {/* Tab-Navigation */}
                <div className="flex border-b">
                  <button
                    type="button"
                    className={`flex-1 py-2 px-4 text-center ${!isPreview ? 'bg-gray-50 font-medium text-emerald-600' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                    onClick={() => setIsPreview(false)}
                  >
                    Bearbeiten
                  </button>
                  <button
                    type="button"
                    className={`flex-1 py-2 px-4 text-center ${isPreview ? 'bg-gray-50 font-medium text-emerald-600' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                    onClick={() => setIsPreview(true)}
                  >
                    Vorschau
                  </button>
                </div>
                
                {!isPreview ? (
                  <>
                    {/* Toolbar */}
                    <EditorToolbar
                      editor={editorRef}
                      onBold={handleBold}
                      onItalic={handleItalic}
                      onH2={handleH2}
                      onH3={handleH3}
                      onUnorderedList={handleUnorderedList}
                      onOrderedList={handleOrderedList}
                      onQuote={handleQuote}
                      onLink={handleLink}
                      onImage={handleImage}
                      onCode={handleCode}
                    />
                    {/* Textarea für Markdown-Bearbeitung */}
                    <Textarea
                      ref={editorRef}
                      id="content"
                      placeholder="# Dein Artikel-Inhalt hier

Schreibe hier deinen Artikel im **Markdown**-Format. Du kannst die Formatierungstools oben nutzen, um Text zu markieren und zu formatieren."
                      value={content}
                      onChange={e => setContent(e.target.value)}
                      onSelect={handleEditorSelect}
                      onClick={handleEditorSelect}
                      className="min-h-[400px] rounded-none border-none resize-y font-mono"
                    />
                  </>
                ) : (
                  // Vorschau
                  <div className="min-h-[400px] p-4 prose max-w-none bg-white">
                    {content ? (
                      <MarkdownRenderer content={content} />
                    ) : (
                      <p className="text-gray-400 italic">
                        Hier wird die Vorschau deines Artikels angezeigt...
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
            
            {/* Hinweis zum Einreichen des Artikels */}
            <div className="bg-emerald-50 p-6 rounded-xl border border-emerald-200 flex items-start gap-3">
              <Info className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-medium text-emerald-800 mb-1">Artikel einreichen</h4>
                <p className="text-sm text-emerald-700">
                  Wenn du deinen Artikel fertiggestellt hast, klicke auf "Exportieren". Die Markdown-Datei wird heruntergeladen und dein E-Mail-Programm öffnet sich automatisch, um den Artikel an uns zu senden.
                </p>
              </div>
            </div>

            {/* Aktionen */}
            <div className="flex justify-between pt-6 border-t">
              <Button 
                type="button" 
                variant="outline" 
                className="rounded-xl border-gray-200 hover:border-gray-300"
                onClick={() => {
                  if (window.confirm('Möchtest du wirklich alle Eingaben löschen?')) {
                    setTitle('');
                    setExcerpt('');
                    setContent('');
                    setCategories([]);
                    setImageUrl('');
                    setEmail('');
                    clearLocalStorage();
                    toast.success('Alle Eingaben wurden gelöscht');
                  }
                }}
              >
                Zurücksetzen
              </Button>
              
              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  onClick={() => navigate('/blog')}
                  className="rounded-xl border-gray-200 hover:border-gray-300"
                >
                  Abbrechen
                </Button>
                <Button 
                  type="button" 
                  className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300"
                  disabled={!title || !content || isExporting}
                  onClick={handleExport}
                >
                  Exportieren
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Loading-Overlay, wenn KI verarbeitet */}
      {isAiProcessing && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white p-8 rounded-2xl shadow-xl flex flex-col items-center max-w-sm mx-4">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-emerald-500 mb-4"></div>
            <h3 className="text-lg font-semibold mb-2">KI verarbeitet deinen Text</h3>
            <p className="text-gray-600 text-center">
              Bitte warte einen Moment, während die KI deinen Artikel optimiert...
            </p>
          </div>
        </div>
      )}

      {/* Loading-Overlay, wenn Export läuft */}
      {isExporting && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white p-8 rounded-2xl shadow-xl flex flex-col items-center max-w-sm mx-4">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-emerald-500 mb-4"></div>
            <h3 className="text-lg font-semibold mb-2">Markdown wird exportiert...</h3>
            <p className="text-gray-600 text-center">
              Bitte warte einen Moment, während die Markdown-Datei generiert wird...
            </p>
          </div>
        </div>
      )}

      {/* Erfolgs-Overlay nach dem Export */}
      {exportSuccess && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white p-8 rounded-2xl shadow-xl flex flex-col items-center max-w-md mx-4">
            <div className="rounded-full h-12 w-12 bg-emerald-100 text-emerald-600 flex items-center justify-center mb-4">
              <Check className="h-6 w-6" />
            </div>
            <h3 className="text-xl font-semibold mb-2">Export erfolgreich!</h3>
            <p className="text-gray-600 text-center mb-4">
              Dein Artikel wurde als Markdown-Datei exportiert. Bitte sende die Datei per E-Mail an:
            </p>
            
            <div className="bg-gray-100 p-3 rounded-xl w-full flex items-center justify-between mb-4">
              <span className="font-mono text-sm">kontakt@kaufpause.at</span>
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-8 px-2 text-xs rounded-lg"
                onClick={() => {
                  navigator.clipboard.writeText('kontakt@kaufpause.at');
                  toast.success('E-Mail-Adresse kopiert!');
                }}
              >
                <Copy className="h-3.5 w-3.5 mr-1" />
                Kopieren
              </Button>
            </div>
            
            <div className="flex gap-3 mt-2">
              <Button 
                onClick={() => {
                  const subject = encodeURIComponent('KaufPause Blog-Artikel Einreichung');
                  const body = encodeURIComponent(`Hallo KaufPause-Team,

ich möchte gerne einen Blog-Artikel einreichen. Die Markdown-Datei ist angehängt.

Titel: ${title}
Kategorien: ${categories.join(', ')}

Bei Rückfragen stehe ich gerne zur Verfügung.

Mit freundlichen Grüßen,
${email ? email : '[Dein Name]'}`);
                  
                  window.open(`mailto:kontakt@kaufpause.at?subject=${subject}&body=${body}`, '_blank');
                }}
                className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300"
              >
                <Mail className="h-4 w-4 mr-2" />
                E-Mail öffnen
              </Button>
              <Button
                variant="outline"
                onClick={() => setExportSuccess(false)}
                className="rounded-xl border-gray-200 hover:border-gray-300"
              >
                Schließen
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateBlogPost; 